import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

const labBookingSlice = createSlice({
  name: "labBooking",
  initialState: {},
  reducers: {},
});

export default labBookingSlice.reducer;

// GET  LabManagement/Lab_GetLocationList?companyID=X
export const fetchLocations = createAsyncThunk(
  "labBooking/fetchLocations",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/Lab_GetLocationList?companyID=${companyID}`,
    ),
);

// GET  LabManagement/Lab_GetLabListBasedOnLocation?ID=0&locationID=X&companyID=X
export const fetchLabsByLocation = createAsyncThunk(
  "labBooking/fetchLabsByLocation",
  async ({ ID = 0, locationID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/Lab_GetLabListBasedOnLocation?ID=${ID}&locationID=${locationID}&companyID=${companyID}`,
    ),
);

// GET  LabManagement/Get_FacilityAvailability?facilityID=X&companyID=X&fromDate=X&toDate=X
// fromDate/toDate are optional — pass the visible calendar window
// ("yyyy-MM-dd") to only fetch bookings overlapping it.
export const fetchFacilityAvailability = createAsyncThunk(
  "labBooking/fetchFacilityAvailability",
  async ({ facilityID, companyID = 0, fromDate = null, toDate = null } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/Get_FacilityAvailability?facilityID=${facilityID}&companyID=${companyID}${
        fromDate ? `&fromDate=${fromDate}` : ""
      }${toDate ? `&toDate=${toDate}` : ""}`,
    ),
);

// GET  LabManagement/Get_FacilityAvailabilityByDate?companyID=X&date=X
// Every lab for the company plus whichever bookings touch one date, in a
// single call — the "All Labs" grid uses this instead of one call per lab.
export const fetchFacilityAvailabilityByDate = createAsyncThunk(
  "labBooking/fetchFacilityAvailabilityByDate",
  async ({ companyID = 0, date } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/Get_FacilityAvailabilityByDate?companyID=${companyID}&date=${date}`,
    ),
);

// POST LabManagement/Lab_Booking_CreateUpdate
export const createUpdateLabBooking = createAsyncThunk(
  "labBooking/createUpdate",
  async (data) =>
    await apiRequest(
      "POST",
      "LabManagement/Lab_Booking_CreateUpdate",
      data,
      true,
    ),
);
// GET  LabManagement/Get_BookingList?companyID=X
export const fetchBookingList = createAsyncThunk(
  "labBooking/fetchBookingList",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/Get_BookingList?companyID=${companyID}`,
    ),
);

// POST LabManagement/Lab_Booking_Approve_Reject
export const approveRejectBooking = createAsyncThunk(
  "labBooking/approveRejectBooking",
  async ({
    bookingID,
    approveRejectEmpID,
    actionName,
    companyID = 0,
    remarks,
  }) =>
    await apiRequest(
      "POST",
      `LabManagement/Lab_Booking_Approve_Reject?bookingID=${bookingID}&approveRejectEmpID=${approveRejectEmpID}&actionName=${actionName}&companyID=${companyID}${remarks ? `&rejectRemarks=${encodeURIComponent(remarks)}` : ""}`,
    ),
);


// GET LabManagement/GetAssociateMapping?facilityId=X&startDate=X&endDate=X
export const fetchAssociateMapping = createAsyncThunk(
  "labBooking/fetchAssociateMapping",
  async ({ facilityId = 0, startDate, endDate } = {}) => {
    const fmt = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      );
    };

    return await apiRequest(
      "GET",
      `LabManagement/GetAssociateMapping?facilityId=${facilityId}&startDate=${encodeURIComponent(fmt(startDate))}&endDate=${encodeURIComponent(fmt(endDate))}`,
    );
  },
);


export const fetchEmployeesByFacility = createAsyncThunk(
  "labBooking/fetchEmployeesByFacility",       
  async ({ facilityId, companyId }, { rejectWithValue }) => {
    try {
      return await apiRequest(                 
        "GET",
        `LabManagement/GetEmployeeByFacility?facilityId=${facilityId}&companyId=${companyId}` 
      );
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch employees");
    }
  }
);

export const fetchLabBookingChartData = createAsyncThunk(
  "labBooking/fetchChartData",
  async ({ companyID = 0, facilityId = 0, locationId = 0, empId = null } = {}) => {
    const params = new URLSearchParams({
      companyId: companyID,
      facilityId,
      locationId,
    });
    if (empId !== null) params.append("EmpId", empId);
    return await apiRequest(
      "GET",
      `LabManagement/GetLabBookingChartData?${params.toString()}`,
    );
  }
);

// GET LabManagement/GetLabBookingData?companyId=X&projectId=X
export const fetchLabBookingData = createAsyncThunk(
  "labBooking/fetchLabBookingData",
  async ({ companyID = 0, projectId = null } = {}) => {
    let url = `LabManagement/GetLabBookingData?companyId=${companyID}`;
    if (projectId) {
      url += `&projectId=${projectId}`;
    }
    return await apiRequest("GET", url);
  },
);



export const fetchGanttChartByLocation = createAsyncThunk(
  "labBooking/fetchGanttChartByLocation",
  async ({ startDateTime = "", endDateTime = "", companyID = 0 } = {}) => {
    const params = new URLSearchParams({ companyID });
    if (startDateTime) params.append("startDateTime", startDateTime);
    if (endDateTime)   params.append("endDateTime",   endDateTime);
    return await apiRequest(
      "GET",
      `LabManagement/Get_GanttChart_Data_ByLocation?${params.toString()}`,
    );
  }
);


export const fetchSetupUtilLabList = createAsyncThunk(
  "labBooking/fetchSetupUtilLabList",
  async ({ companyID = 0, employeeID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/SetupUtil_GetLabList?companyID=${companyID}&employeeID=${employeeID}`,
    ),
);
export const fetchSetupUtilCreateData = createAsyncThunk(
  "labBooking/fetchSetupUtilCreateData",
  async ({ facilityID = 0, companyID = 0, employeeID = 0, adhoc = false } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/SetupUtil_GetDetailsForCreate?facilityID=${facilityID}&companyID=${companyID}&employeeID=${employeeID}&adhoc=${adhoc ? "true" : "false"}`,
    ),
);
export const fetchSetupUtilTestDetails = createAsyncThunk(
  "labBooking/fetchSetupUtilTestDetails",
  async ({ facilityID = 0, companyID = 0, projectID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `LabManagement/SetupUtil_GetTestDetails?facilityID=${facilityID}&companyID=${companyID}&projectID=${projectID}`,
    ),
);
// POST LabManagement/SetupUtil_SaveTimesheet
export const saveSetupUtilTimesheet = createAsyncThunk(
  "labBooking/saveSetupUtilTimesheet",
  async (data) =>
    await apiRequest(
      "POST",
      "LabManagement/SetupUtil_SaveTimesheet",
      data,
      true,
    ),
);

// POST LabManagement/SetupUtil_Timesheetlist
export const fetchSetupUtilTimesheetList = createAsyncThunk(
  "labBooking/fetchSetupUtilTimesheetList",
  async ({ facilityID = 0, employeeID = 0, companyID = 0, selDate = "", moveFlag = "", viewFlag = "" } = {}) =>
    await apiRequest(
      "POST",
      "LabManagement/SetupUtil_Timesheetlist",
      { facilityID, employeeID, companyID, selDate, moveFlag, viewFlag },
    ),
);

// POST LabManagement/SetupUtil_CheckFacilityLog
export const checkSetupUtilFacilityLog = createAsyncThunk(
  "labBooking/checkSetupUtilFacilityLog",
  async ({ facID = 0, employeeID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "POST",
      `LabManagement/SetupUtil_CheckFacilityLog?facID=${facID}&employeeID=${employeeID}&CompanyId=${companyID}`,
    ),
);

// POST LabManagement/SetupUtil_UpdateTemperature
export const updateSetupUtilTemperature = createAsyncThunk(
  "labBooking/updateSetupUtilTemperature",
  async ({ facID = 0, temperature = 0, humidity = 0, employeeID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "POST",
      `LabManagement/SetupUtil_UpdateTemperature?FacID=${facID}&Temperature=${temperature}&Humitity=${humidity}&EmployeeID=${employeeID}&CompanyId=${companyID}`,
    ),
);
